import React, { useCallback, useState } from 'react';
import {
  FormControl,
  FormLabel,
  Input,
  Box,
  List,
  ListItem,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { useGooglePlacesAutocomplete } from 'hooks/useGooglePlacesAutocomplete';

const GooglePlacesAutocomplete = ({
  value,
  onChange,
  onPlaceSelected,
  labelText,
  placeholder,
  textColor,
  errors,
  isRequired = false,
}) => {
  const { isLoaded, getPlacePredictions, getPlaceDetails } =
    useGooglePlacesAutocomplete();

  const [suggestions, setSuggestions] = useState([]);
  const [isDropdownVisible, setDropdownVisible] = useState(false);

  // Fetch suggestions as user types
  const handleInputChange = useCallback(
    (e) => {
      const inputValue = e.target.value;
      onChange(inputValue);

      if (!inputValue) {
        setSuggestions([]);
        setDropdownVisible(false);
        return;
      }

      getPlacePredictions(
        {
          input: inputValue,
          types: ['(regions)'],
        },
        (predictions) => {
          if (predictions && predictions.length > 0) {
            setSuggestions(predictions);
            setDropdownVisible(true);
          } else {
            setSuggestions([]);
            setDropdownVisible(false);
          }
        },
      );
    },
    [onChange, getPlacePredictions],
  );

  // Fetch full place details after selecting a suggestion
  const handleSuggestionClick = useCallback(
    (prediction) => {
      getPlaceDetails(
        {
          placeId: prediction.place_id,
          fields: ['address_components', 'name'],
        },
        (place) => {
          const components = place.address_components || [];

          const regionComponent = components.find((c) =>
            c.types.includes('administrative_area_level_1'),
          );

          const regionCode = regionComponent ? regionComponent.short_name : '';
          const regionName = regionComponent
            ? regionComponent.long_name
            : place.name;

          onChange(regionName);
          onPlaceSelected({
            name: regionName,
            regionCode,
            place,
          });

          setSuggestions([]);
          setDropdownVisible(false);
        },
      );
    },
    [onChange, onPlaceSelected, getPlaceDetails],
  );

  if (!isLoaded) {
    return (
      <FormControl isInvalid={errors} isRequired={isRequired}>
        <FormLabel color={textColor}>{labelText}</FormLabel>
        <Input placeholder="Loading..." disabled color={textColor} />
      </FormControl>
    );
  }

  return (
    <FormControl isInvalid={errors} isRequired={isRequired} position="relative">
      <FormLabel fontSize="sm" color={textColor} fontWeight="medium">
        {labelText}
      </FormLabel>

      <Input
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder}
        color={textColor}
        borderColor="gray.300"
        _focus={{
          borderColor: 'reviveOrange',
          boxShadow: '0 0 0 1px reviveOrange',
        }}
        _hover={{ borderColor: 'gray.400' }}
      />

      {/* Dropdown Suggestion Box */}
      {isDropdownVisible && suggestions.length > 0 && (
        <Box
          border="1px solid #ccc"
          borderRadius="md"
          bg="white"
          mt={1}
          maxH="200px"
          overflowY="auto"
          zIndex={1000}
          position="absolute"
          width="100%"
        >
          <List>
            {suggestions.map((item) => (
              <ListItem
                key={item.place_id}
                p={2}
                _hover={{ background: 'gray.200', cursor: 'pointer' }}
                onClick={() => handleSuggestionClick(item)}
              >
                {item.description}
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {errors && (
        <p style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
          {errors}
        </p>
      )}
    </FormControl>
  );
};

GooglePlacesAutocomplete.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onPlaceSelected: PropTypes.func.isRequired,
  labelText: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  textColor: PropTypes.string.isRequired,
  errors: PropTypes.string,
  isRequired: PropTypes.bool,
};

export default GooglePlacesAutocomplete;
